import { useContext, useState, useEffect, useCallback } from 'react';
import { useParams, RouterContext, goTo } from 't-lib-ui/router';
import MainLayout from '../../components/MainLayout';
import { FlowForm, FlowFormLeaveContext, ButtonBar, useFlowFormGlobal, registerKeyHandler, unregisterKeyHandler } from 't-lib-ui/structure';
import { TextButton, Input, DropDownSelector, DropDownSelectorValue, Note } from 't-lib-ui/widgets';
import { saveIcon, trashIcon } from '../../components/icons';
import AdminSecretModal from '../../components/admin/AdminSecretModal';
import AdminHeader from '../../components/admin/AdminHeader';
import { sidebarLeaveHandler } from '../../components/LeftSidebarFocus';
import { AppState } from '../../context/general';
import { nonNull } from 't-lib-ui/helpers';
import { getConfig } from '../../connector/auth';
import { adminListInvitations, adminCreateInvitation, adminUpdateInvitation, adminDeleteInvitation } from '../../connector/api';
import { RestInvitation } from '../../schema-copy/auth-schema';

const planOptions: DropDownSelectorValue[] = [
  { id: 'free', text: 'Free', searchTextLc: 'free' },
  { id: 'pro', text: 'Pro', searchTextLc: 'pro' },
  { id: 'vip', text: 'VIP', searchTextLc: 'vip' },
  { id: 'earlyAdopter', text: 'Early Adopter', searchTextLc: 'early adopter' },
];

const slug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'invite';
const random8 = () => Math.floor(10000000 + Math.random() * 90000000).toString();
const isoToDateInput = (iso?: string) => iso ? iso.slice(0, 10) : '';
const dateInputToIso = (d: string) => d ? new Date(d + 'T00:00:00').toISOString() : '';

const AdminViewInvitationPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isNew = id == 'new';
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);

  const [adminSecret, setAdminSecret] = useState<string | null>(null);
  const [authFailed, setAuthFailed] = useState(0);
  const [invitation, setInvitation] = useState<RestInvitation | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const [suffix] = useState(random8);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [codeEdited, setCodeEdited] = useState(false);
  const [capacity, setCapacity] = useState('1');
  const [planCode, setPlanCode] = useState('free');
  const [expires, setExpires] = useState('');

  const [confirmDelete, setConfirmDelete] = useState(false);

  const isDefault = !!invitation?.isDefault;

  useFlowFormGlobal();

  useEffect(() => {
    registerKeyHandler('adminViewInvitationPage', {
      'Escape': () => { goTo(routerCtx, '/a/admin/invitations'); return true; },
    }, false, 0);
    return () => unregisterKeyHandler('adminViewInvitationPage');
  }, [routerCtx]);

  const load = useCallback(async (secret: string) => {
    if (isNew) return;
    try {
      setLoading(true);
      setError(undefined);
      const list = await adminListInvitations(nonNull(appState.state.authData), secret);
      const found = list.find(i => i.id == id);
      if (!found) { setError('Invitation not found'); return; }
      setInvitation(found);
      setName(found.name);
      setCode(found.code);
      setCodeEdited(true);
      setCapacity(String(found.capacity));
      setPlanCode(found.planCode);
      setExpires(isoToDateInput(found.expiresAt));
    } catch (e: any) {
      if (e?.status == 401) setAuthFailed(n => n + 1);
      setError(e instanceof Error ? e.message : 'Failed to load invitation');
    } finally {
      setLoading(false);
    }
  }, [appState.state.authData, id, isNew]);

  useEffect(() => {
    if (adminSecret) load(adminSecret);
  }, [adminSecret, load]);

  // For a new invitation, keep the code in sync with the name until the admin
  // edits it by hand. The 8-digit suffix is always preserved.
  const handleNameChange = (value: string) => {
    setName(value);
    if (isNew && !codeEdited)
      setCode(`${slug(value)}-${suffix}`);
  };

  const handleSave = async () => {
    if (!adminSecret || saving) return;
    const cap = Math.max(0, Math.floor(Number(capacity) || 0));
    try {
      setSaving(true);
      setError(undefined);
      const auth = nonNull(appState.state.authData);
      if (isNew) {
        await adminCreateInvitation(auth, { name: name.trim(), code: code.trim(), capacity: cap, planCode, expiresAt: expires ? dateInputToIso(expires) : undefined }, adminSecret);
      } else {
        await adminUpdateInvitation(auth, id!, {
          name: name.trim(),
          ...(isDefault ? {} : { code: code.trim() }),
          capacity: cap,
          planCode,
          expiresAt: expires ? dateInputToIso(expires) : '',
        }, adminSecret);
      }
      goTo(routerCtx, '/a/admin/invitations');
    } catch (e: any) {
      if (e?.status == 401) setAuthFailed(n => n + 1);
      setError(e instanceof Error ? e.message : 'Failed to save invitation');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!adminSecret || saving || !invitation) return;
    try {
      setSaving(true);
      setError(undefined);
      await adminDeleteInvitation(nonNull(appState.state.authData), invitation.id, adminSecret);
      goTo(routerCtx, '/a/admin/invitations');
    } catch (e: any) {
      setError(e instanceof Error ? e.message : 'Failed to delete invitation');
      setSaving(false);
    }
  };

  const inviteLink = code ? `${getConfig().webHost}/signup?i=${encodeURIComponent(code)}` : '';

  return (
    <>
      <AdminSecretModal onSecretVerified={setAdminSecret} authFailed={authFailed} />
      {adminSecret && (
        <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
          <MainLayout
            selectedFeed="admin-invitations"
            right={({ onExpand }) => (
              <FlowForm main={true} className='full-margin'>
                <style>{`
                  .invitation-form { max-width: 700px; margin: 0 auto; }
                  .invitation-field { margin-bottom: 24px; }
                  .invitation-label { display: block; font-weight: 600; margin-bottom: 8px; color: var(--gray-350); font-size: 0.95rem; }
                  .invitation-date { font: inherit; padding: 8px 10px; border: 1px solid var(--gray-800); border-radius: 4px; }
                  .invitation-link { background: var(--gray-975); border-radius: 4px; padding: 12px 16px; word-break: break-all; font-size: 0.9em; }
                `}</style>
                <div className="invitation-form">
                  <AdminHeader title={isNew ? 'Create Invitation' : 'Edit Invitation'} onExpand={onExpand} />

                  <ButtonBar size="small">
                    <TextButton label="Save" icon={saveIcon} onClick={handleSave} focussable="flow" type="major" busy={saving} enabled={loading ? 'disabled' : 'enabled'} />
                    {!isNew && !isDefault && !confirmDelete && (
                      <TextButton label="Delete…" icon={trashIcon} onClick={() => setConfirmDelete(true)} focussable="flow" enabled={saving ? 'disabled' : 'enabled'} />
                    )}
                  </ButtonBar>

                  {error && <Note type='error'>{error}</Note>}
                  {isDefault && <Note type='info'>This is the default invitation used for sign-ups without a code. Its code can't be changed and it can't be deleted.</Note>}

                  {confirmDelete && (
                    <div style={{ padding: '16px', margin: '16px 0', background: '#fff3cd', border: '2px solid #ffc107', borderRadius: '4px' }}>
                      <div style={{ marginBottom: '12px', color: '#856404', fontWeight: 600 }}>Delete this invitation? This can't be undone.</div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <TextButton label="Yes, delete" onClick={handleDelete} focussable="flow" type="major" busy={saving} />
                        <TextButton label="Cancel" onClick={() => setConfirmDelete(false)} focussable="flow" />
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: '24px' }}>
                    <div className="invitation-field">
                      <label className="invitation-label">Name</label>
                      <Input value={name} onInput={handleNameChange} placeholder="e.g. Summer promo" layout="singleCol" maxLength={128} />
                    </div>

                    {!isDefault && (
                      <div className="invitation-field">
                        <label className="invitation-label">Code</label>
                        <Input value={code} onInput={v => { setCode(v); setCodeEdited(true); }} placeholder="code-12345678" layout="singleCol" maxLength={128} />
                      </div>
                    )}

                    <div className="invitation-field">
                      <label className="invitation-label">Plan</label>
                      <DropDownSelector selections={planOptions} value={planCode} onSelect={(v: DropDownSelectorValue) => setPlanCode(v.id)} focussable="flow" />
                    </div>

                    <div className="invitation-field">
                      <label className="invitation-label">Capacity {!isDefault && <span style={{ fontWeight: 400, color: 'var(--gray-500)', fontSize: '0.85em' }}>(remaining sign-ups)</span>}</label>
                      <Input value={capacity} onInput={setCapacity} type="number" min={0} layout="singleCol" />
                    </div>

                    <div className="invitation-field">
                      <label className="invitation-label">Expires <span style={{ fontWeight: 400, color: 'var(--gray-500)', fontSize: '0.85em' }}>(leave empty for never)</span></label>
                      <div>
                        <input type="date" className="invitation-date flowFocussable" value={expires} onChange={e => setExpires(e.target.value)} />
                      </div>
                    </div>

                    {inviteLink && (
                      <div className="invitation-field">
                        <label className="invitation-label">Invite link</label>
                        <div className="invitation-link">{inviteLink}</div>
                      </div>
                    )}
                  </div>
                </div>
              </FlowForm>
            )}
            startWithLeftCollapsed={false}
          />
        </FlowFormLeaveContext.Provider>
      )}
    </>
  );
};

export default AdminViewInvitationPage;
